import { ApiService } from '@/shared/services/api.service';
import { RCMember, RCMessage, RCPresenceStatus, RCRoom } from '../types/rocketchat.types';
import { getRcToken, getRcUserId } from './rcCredentials';
import { getRcBaseUrl } from './rcBaseUrl';

const base = () => getRcBaseUrl();

const headers = (): HeadersInit => ({
  'X-Auth-Token': getRcToken(),
  'X-User-Id': getRcUserId(),
  'Content-Type': 'application/json',
});

const rcFetch = <T>(path: string, signal?: AbortSignal): Promise<T> =>
  fetch(`${base()}${path}`, { headers: headers(), signal }).then((r) => r.json());

const rcPost = <T>(path: string, body: object): Promise<T> =>
  fetch(`${base()}${path}`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(body),
  }).then((r) => r.json());

const historyRoute: Record<string, string> = { c: 'channels', p: 'groups', d: 'im' };

// Membership management (invite/kick/leave/roles) only applies to channels ('c')
// and private groups ('p') — RC has no such endpoints for direct messages ('d').
const membershipEndpoint = (roomType: string): 'channels' | 'groups' => {
  if (roomType === 'p') return 'groups';
  if (roomType === 'c') return 'channels';
  throw new Error(`Membership management is not supported for room type "${roomType}"`);
};

// ── Spotlight cache ───────────────────────────────────────────────────────────
// Keyed by search term. TTL prevents stale results; in-flight Map collapses
// concurrent calls for the same term into one network round-trip.

type SpotlightResult = {
  users: {
    _id: string;
    username: string;
    name: string;
    status?: string;
    email?: string;
  }[];
  rooms: {
    _id: string;
    name: string;
    fname?: string;
    t: string;
    prid?: string;
    teamMain?: boolean;
    archived?: boolean;
  }[];
};

const SPOTLIGHT_TTL_MS = 30_000;
const spotlightCache = new Map<string, { result: SpotlightResult; expiresAt: number }>();
const spotlightInflight = new Map<string, Promise<SpotlightResult>>();

// Single source of truth for user search everywhere in the app (header search,
// discussion members, "New direct message"): our backend resolves real RC
// usernames and applies org/platform-role scoping — RC's own public /spotlight
// endpoint doesn't return email and can't be trusted to scope by org.
const searchBackendUsers = (
  term: string,
  signal?: AbortSignal
): Promise<{ _id: string; username: string; name: string }[]> =>
  ApiService.get<{ _id: string; username: string; name: string }[]>(
    `rocketchatauth/users/search?term=${encodeURIComponent(term)}&limit=20`,
    { signal }
  );

// Resolves a NexGen user pid to their real RC username via our backend (which
// looks it up through RC's own users.info API) — a pid alone isn't
// chat-usable, and the RC username can't be reliably derived from an email
// client-side (may differ from the naive local-part_domain guess).
const resolveBackendUserByPid = (
  pid: string
): Promise<{ username: string; name?: string } | null> =>
  ApiService.get<{ username: string; name?: string } | null>(
    `rocketchatauth/users/by-pid?pid=${encodeURIComponent(pid)}`
  );

const fetchSpotlight = (term: string, signal?: AbortSignal): Promise<SpotlightResult> => {
  const cached = spotlightCache.get(term);
  if (cached && Date.now() < cached.expiresAt) return Promise.resolve(cached.result);

  const existing = spotlightInflight.get(term);
  if (existing) return existing;

  const promise = Promise.allSettled([
    // Rooms — RC's own search, called directly. By design this only surfaces
    // rooms the caller could newly join (public channels/teams), not rooms
    // they're already a member of — RC's own client search overlays those
    // separately from its locally-synced subscription list, which is what
    // SidebarTopBar's spotlightResults does too (merged with chatPanel.store's
    // already-loaded rooms — see mergeLocalRoomMatches in SidebarTopBar.tsx).
    rcFetch<any>(`/api/v1/spotlight?query=${encodeURIComponent(term)}`, signal),
    searchBackendUsers(term, signal),
  ]).then(([spotlightRes, usersRes]) => {
    spotlightInflight.delete(term);

    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

    if (spotlightRes.status === 'rejected') {
      console.warn('[RocketChatRESTService] spotlight room search failed:', spotlightRes.reason);
    }
    const json = spotlightRes.status === 'fulfilled' ? spotlightRes.value : {};
    if (spotlightRes.status === 'fulfilled' && !Array.isArray(json.rooms)) {
      // RC responded (no network/CORS failure) but the payload wasn't the
      // expected shape — most likely an auth/permission error body
      // (`{success:false, error:...}`) rather than an actual empty result.
      console.warn(
        '[RocketChatRESTService] spotlight room search returned unexpected shape:',
        json
      );
    }
    const allRooms: SpotlightResult['rooms'] = Array.isArray(json.rooms) ? json.rooms : [];
    const users: SpotlightResult['users'] =
      usersRes.status === 'fulfilled' ? usersRes.value.map((u) => ({ _id: u.username, ...u })) : [];

    const result: SpotlightResult = { users, rooms: allRooms };
    spotlightCache.set(term, { result, expiresAt: Date.now() + SPOTLIGHT_TTL_MS });
    return result;
  });

  spotlightInflight.set(term, promise);
  return promise;
};

export const RocketChatRESTService = {
  getRoomHistory: async (
    roomId: string,
    type: 'c' | 'p' | 'd',
    before?: string,
    count = 50,
    signal?: AbortSignal
  ): Promise<RCMessage[]> => {
    const params = new URLSearchParams({ roomId, count: String(count) });
    // RC's `latest` is the upper bound (messages older than it); `oldest` is the
    // lower bound and would just re-return the newest page.
    if (before) params.set('latest', before);
    const json: any = await fetch(
      `${base()}/api/v1/${historyRoute[type] ?? 'channels'}.history?${params}`,
      { headers: headers(), signal }
    ).then((r) => r.json());
    return Array.isArray(json.messages) ? json.messages : [];
  },

  // A single username creates a normal 1:1 DM; multiple usernames create a
  // multi-person direct message (RC groups 3+ participants into one DM room).
  createDM: async (usernames: string | string[]): Promise<{ rid: string; room: any | null }> => {
    const body = Array.isArray(usernames)
      ? { usernames: usernames.join(',') }
      : { username: usernames };
    const json: any = await rcPost('/api/v1/dm.create', body);
    const rid = json.room?._id || json.direct?._id || '';
    return { rid, room: json.room || null };
  },

  uploadFile: async (
    roomId: string,
    file: File,
    description = '',
    tmid?: string
  ): Promise<void> => {
    const formData = new FormData();
    formData.append('file', file);
    if (description) formData.append('description', description);
    const r = await fetch(`${base()}/api/v1/rooms.media/${roomId}`, {
      method: 'POST',
      headers: {
        'X-Auth-Token': getRcToken(),
        'X-User-Id': getRcUserId(),
      },
      body: formData,
    });
    if (!r.ok) {
      const body = await r.json().catch(() => ({}));
      throw new Error(body?.error || body?.message || `Upload failed (${r.status})`);
    }
    const json = await r.json().catch(() => ({}));
    if (json.file?._id) {
      const { RocketChatDDPServiceInstance } = await import('./RocketChatDDPService');
      await RocketChatDDPServiceInstance.sendFileMessage(
        roomId,
        {
          _id: json.file._id,
          type: file.type,
          size: file.size,
          name: file.name,
          url: json.file.url,
        },
        description,
        tmid
      );
    }
  },

  sendMessage: async (
    roomId: string,
    msg: string,
    tmid?: string,
    clientMsgId?: string
  ): Promise<RCMessage> => {
    const message: any = { rid: roomId, msg };
    if (tmid) message.tmid = tmid;
    if (clientMsgId) message._id = clientMsgId;
    const json: any = await rcPost('/api/v1/chat.sendMessage', { message });
    return json.message as RCMessage;
  },

  searchMessages: async (roomId: string, searchText: string): Promise<RCMessage[]> => {
    // Wrap in regex for substring matching — RC's default full-text search only matches whole words
    const escaped = searchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const params = new URLSearchParams({ roomId, searchText: `/${escaped}/i` });
    const json: any = await rcFetch(`/api/v1/chat.search?${params}`);
    return Array.isArray(json.messages) ? json.messages : [];
  },

  getThreadMessages: async (tmid: string): Promise<RCMessage[]> => {
    const json: any = await rcFetch(`/api/v1/chat.getThreadMessages?tmid=${tmid}&count=500`);
    return Array.isArray(json.messages) ? json.messages : [];
  },

  getThreadsList: async (roomId: string): Promise<RCMessage[]> => {
    const params = new URLSearchParams({ rid: roomId, count: '50' });
    const json: any = await rcFetch(`/api/v1/chat.getThreadsList?${params}`);
    return Array.isArray(json.threads) ? json.threads : [];
  },

  getDiscussions: async (roomId: string): Promise<(RCRoom & { msgs: number })[]> => {
    const params = new URLSearchParams({ roomId, count: '50' });
    const json: any = await rcFetch(`/api/v1/rooms.getDiscussions?${params}`);
    return Array.isArray(json.discussions) ? json.discussions : [];
  },

  getRoomMembers: async (
    roomId: string,
    roomType: string,
    offset = 0,
    count = 100
  ): Promise<RCMember[]> => {
    const endpoint = roomType === 'p' ? 'groups' : roomType === 'd' ? 'im' : 'channels';
    const params = new URLSearchParams({
      roomId,
      offset: String(offset),
      count: String(count),
    });
    const json: any = await rcFetch(`/api/v1/${endpoint}.members?${params}`);
    return Array.isArray(json.members) ? json.members : [];
  },

  // Per-member room roles (owner/moderator/leader) — separate from getRoomMembers
  // since RC's `.members` endpoint doesn't include roles.
  getRoomRoles: async (
    roomId: string,
    roomType: string
  ): Promise<{ _id: string; username: string; roles: string[] }[]> => {
    const endpoint = membershipEndpoint(roomType);
    const json: any = await rcFetch(`/api/v1/${endpoint}.roles?roomId=${roomId}`);
    return Array.isArray(json.roles)
      ? json.roles.map((r: any) => ({
          _id: r.u?._id || '',
          username: r.u?.username || '',
          roles: r.roles || [],
        }))
      : [];
  },

  // RC's invite/kick/addOwner endpoints require the target's real RC _id, not
  // just a username, so each of these resolves via getUserInfo first.
  addMembers: async (
    roomId: string,
    roomType: string,
    usernames: string[]
  ): Promise<{ added: string[]; failed: string[] }> => {
    const endpoint = membershipEndpoint(roomType);
    const added: string[] = [];
    const failed: string[] = [];
    for (const username of usernames) {
      try {
        const user = await RocketChatRESTService.getUserInfo(username);
        if (!user?._id) {
          failed.push(username);
          continue;
        }
        const json: any = await rcPost(`/api/v1/${endpoint}.invite`, {
          roomId,
          userId: user._id,
        });
        if (json?.success) added.push(username);
        else failed.push(username);
      } catch {
        failed.push(username);
      }
    }
    return { added, failed };
  },

  removeMember: async (roomId: string, roomType: string, username: string): Promise<void> => {
    const endpoint = membershipEndpoint(roomType);
    const user = await RocketChatRESTService.getUserInfo(username);
    if (!user?._id) throw new Error(`User not found: ${username}`);
    const json: any = await rcPost(`/api/v1/${endpoint}.kick`, { roomId, userId: user._id });
    if (!json?.success) throw new Error(json?.error || 'Failed to remove member');
  },

  leaveRoom: async (roomId: string, roomType: string): Promise<void> => {
    const endpoint = membershipEndpoint(roomType);
    const json: any = await rcPost(`/api/v1/${endpoint}.leave`, { roomId });
    if (!json?.success) throw new Error(json?.error || 'Failed to leave group');
  },

  addOwner: async (roomId: string, roomType: string, username: string): Promise<void> => {
    const endpoint = membershipEndpoint(roomType);
    const user = await RocketChatRESTService.getUserInfo(username);
    if (!user?._id) throw new Error(`User not found: ${username}`);
    const json: any = await rcPost(`/api/v1/${endpoint}.addOwner`, { roomId, userId: user._id });
    if (!json?.success) throw new Error(json?.error || 'Failed to set owner');
  },

  removeOwner: async (roomId: string, roomType: string, username: string): Promise<void> => {
    const endpoint = membershipEndpoint(roomType);
    const user = await RocketChatRESTService.getUserInfo(username);
    if (!user?._id) throw new Error(`User not found: ${username}`);
    const json: any = await rcPost(`/api/v1/${endpoint}.removeOwner`, { roomId, userId: user._id });
    if (!json?.success) throw new Error(json?.error || 'Failed to remove owner');
  },

  // Auditorium's "Lock chat" control (NGE-804) does NOT call this directly —
  // it goes through the backend (POST auditorium/:pid/chat/lock), since RC's
  // setReadOnly requires the caller's own owner/admin role, and this method
  // is kept here only for API-surface consistency / future non-Auditorium use.
  setReadOnly: async (roomId: string, roomType: string, readOnly: boolean): Promise<void> => {
    const endpoint = membershipEndpoint(roomType);
    const json: any = await rcPost(`/api/v1/${endpoint}.setReadOnly`, { roomId, readOnly });
    if (!json?.success) throw new Error(json?.error || 'Failed to set read-only');
  },

  addModerator: async (roomId: string, roomType: string, username: string): Promise<void> => {
    const endpoint = membershipEndpoint(roomType);
    const user = await RocketChatRESTService.getUserInfo(username);
    if (!user?._id) throw new Error(`User not found: ${username}`);
    const json: any = await rcPost(`/api/v1/${endpoint}.addModerator`, {
      roomId,
      userId: user._id,
    });
    if (!json?.success) throw new Error(json?.error || 'Failed to set moderator');
  },

  removeModerator: async (roomId: string, roomType: string, username: string): Promise<void> => {
    const endpoint = membershipEndpoint(roomType);
    const user = await RocketChatRESTService.getUserInfo(username);
    if (!user?._id) throw new Error(`User not found: ${username}`);
    const json: any = await rcPost(`/api/v1/${endpoint}.removeModerator`, {
      roomId,
      userId: user._id,
    });
    if (!json?.success) throw new Error(json?.error || 'Failed to remove moderator');
  },

  getRoomFiles: async (
    roomId: string,
    roomType: string
  ): Promise<
    {
      _id: string;
      name: string;
      size: number;
      uploadedAt: string;
      url?: string;
      user?: { username: string };
    }[]
  > => {
    const endpoint = roomType === 'd' ? 'im' : roomType === 'p' ? 'groups' : 'channels';
    const params = new URLSearchParams({ roomId, count: '50' });
    const json: any = await rcFetch(`/api/v1/${endpoint}.files?${params}`);
    return Array.isArray(json.files) ? json.files : [];
  },

  getPinnedMessages: async (roomId: string): Promise<RCMessage[]> => {
    const params = new URLSearchParams({ roomId, count: '50' });
    const json: any = await rcFetch(`/api/v1/chat.getPinnedMessages?${params}`);
    return Array.isArray(json.messages) ? json.messages : [];
  },

  getStarredMessages: async (roomId: string): Promise<RCMessage[]> => {
    const params = new URLSearchParams({ roomId, count: '50' });
    const json: any = await rcFetch(`/api/v1/chat.getStarredMessages?${params}`);
    return Array.isArray(json.messages) ? json.messages : [];
  },

  getRoomInfo: async (
    roomId: string
  ): Promise<{
    _id: string;
    name: string;
    fname?: string;
    usernames?: string[];
    usersCount?: number;
    description?: string;
    topic?: string;
    announcement?: string;
  } | null> => {
    try {
      const json: any = await rcFetch(`/api/v1/rooms.info?roomId=${roomId}`);
      return json.room ?? null;
    } catch {
      return null;
    }
  },

  getUserInfo: async (
    username: string
  ): Promise<{
    _id: string;
    username: string;
    name: string;
    status?: string;
    statusText?: string;
    bio?: string;
    emails?: { address: string }[];
  } | null> => {
    try {
      const json: any = await rcFetch(
        `/api/v1/users.info?username=${encodeURIComponent(username)}`
      );
      return json.user ?? null;
    } catch {
      return null;
    }
  },

  searchUsers: async (
    term: string,
    signal?: AbortSignal
  ): Promise<{ _id: string; username: string; name: string }[]> => {
    // Backend returns the real RC _id — presence/NexGen status are keyed by it.
    return searchBackendUsers(term, signal);
  },

  resolveUserByPid: (pid: string): Promise<{ username: string; name?: string } | null> =>
    resolveBackendUserByPid(pid),

  spotlight: (term: string, signal?: AbortSignal): Promise<SpotlightResult> =>
    fetchSpotlight(term, signal),

  createChannel: async (name: string, members: string[] = []): Promise<{ rid: string }> => {
    const json: any = await rcPost('/api/v1/channels.create', { name, members });
    return { rid: json.channel?._id || '' };
  },

  createPrivateGroup: async (name: string, members: string[] = []): Promise<{ rid: string }> => {
    const json: any = await rcPost('/api/v1/groups.create', { name, members });
    return { rid: json.group?._id || '' };
  },

  createTeam: async (
    name: string,
    isPrivate: boolean,
    members: string[] = []
  ): Promise<{ rid: string }> => {
    const json: any = await rcPost('/api/v1/teams.create', {
      name,
      type: isPrivate ? 1 : 0,
      members,
    });
    return { rid: json.team?.roomId || '' };
  },

  createDiscussion: async (opts: {
    prid: string;
    t_name: string;
    topic?: string;
    users?: string[];
    reply?: string;
  }): Promise<{ rid: string }> => {
    const json: any = await rcPost('/api/v1/rooms.createDiscussion', opts);
    return { rid: json.discussion?._id || '' };
  },

  fetchAllMessages: async (roomId: string, type: 'c' | 'p' | 'd'): Promise<RCMessage[]> => {
    const all: RCMessage[] = [];
    const route = historyRoute[type] ?? 'channels';
    let latestParam: string | undefined;

    while (true) {
      const params = new URLSearchParams({ roomId, count: '100' });
      if (latestParam) params.set('latest', latestParam);
      const json: any = await fetch(`${base()}/api/v1/${route}.history?${params}`, {
        headers: headers(),
      }).then((r) => r.json());
      const msgs: RCMessage[] = Array.isArray(json.messages) ? json.messages : [];
      if (!msgs.length) break;
      all.push(...msgs);
      if (msgs.length < 100) break;
      const ts = msgs[msgs.length - 1].ts;
      latestParam = typeof ts === 'string' ? ts : new Date((ts as any).$date).toISOString();
    }

    return all;
  },

  deleteMessage: async (roomId: string, msgId: string): Promise<void> => {
    const json: any = await rcPost('/api/v1/chat.delete', { roomId, msgId, asUser: true });
    if (!json?.success) throw new Error(json?.error || 'Delete failed');
  },

  // Bulk-delete all messages in a room up to `latest` (ISO string).
  // Requires clean-channel-history permission on the RC server.
  // On success RC fires deleteMessage events to all room subscribers.
  cleanHistory: async (roomId: string, latest: string): Promise<boolean> => {
    try {
      const json: any = await rcPost('/api/v1/rooms.cleanHistory', {
        roomId,
        latest,
        oldest: '1970-01-01T00:00:00.000Z',
        inclusive: true,
        ignoreThreads: false,
        filesOnly: false,
      });
      return json?.success === true;
    } catch {
      return false;
    }
  },

  starMessage: async (msgId: string): Promise<void> => {
    await rcPost('/api/v1/chat.starMessage', { messageId: msgId });
  },

  unStarMessage: async (msgId: string): Promise<void> => {
    await rcPost('/api/v1/chat.unStarMessage', { messageId: msgId });
  },

  followMessage: async (msgId: string): Promise<void> => {
    await rcPost('/api/v1/chat.followMessage', { mid: msgId });
  },

  unfollowMessage: async (msgId: string): Promise<void> => {
    await rcPost('/api/v1/chat.unfollowMessage', { mid: msgId });
  },

  pinMessage: async (msgId: string): Promise<void> => {
    const json: any = await rcPost('/api/v1/chat.pinMessage', { messageId: msgId });
    if (!json?.success) throw new Error(json?.error || 'Pin failed');
  },

  unPinMessage: async (msgId: string): Promise<void> => {
    const json: any = await rcPost('/api/v1/chat.unPinMessage', { messageId: msgId });
    if (!json?.success) throw new Error(json?.error || 'Unpin failed');
  },

  reactMessage: async (msgId: string, emoji: string): Promise<void> => {
    await rcPost('/api/v1/chat.react', { messageId: msgId, emoji });
  },

  closeRoom: async (roomId: string, roomType: string): Promise<void> => {
    const endpoint = roomType === 'p' ? 'groups' : roomType === 'd' ? 'im' : 'channels';
    await rcPost(`/api/v1/${endpoint}.close`, { roomId });
  },

  markRoomAsRead: async (roomId: string): Promise<void> => {
    const json: any = await rcPost('/api/v1/subscriptions.read', {
      rid: roomId,
      readThreads: true,
    });
    if (!json?.success) throw new Error(json?.error || 'Mark as read failed');
  },

  // Unhides a room (reverse of rooms.hide) — RC only reopens DMs implicitly via dm.create.
  openRoom: async (roomId: string, roomType: string): Promise<void> => {
    const endpoint = roomType === 'd' ? 'im' : membershipEndpoint(roomType);
    await rcPost(`/api/v1/${endpoint}.open`, { roomId });
  },

  markRoomAsUnread: async (roomId: string): Promise<void> => {
    await rcPost('/api/v1/subscriptions.unread', { roomId });
  },

  favoriteRoom: async (roomId: string, favorite: boolean): Promise<void> => {
    await rcPost('/api/v1/rooms.favorite', { roomId, favorite });
  },

  getUserAvatarUrl: (usernameOrId: string): string => `${base()}/avatar/${usernameOrId}`,

  setStatus: async (status: RCPresenceStatus, message?: string): Promise<void> => {
    const json: any = await rcPost('/api/v1/users.setStatus', {
      status,
      ...(message !== undefined ? { message } : {}),
    });
    if (!json?.success) throw new Error(json?.error || 'Failed to set status');
  },
};
